import { StyledSelect } from "@ando/ui";
/**
 * PersonaSection — editor for a project's persona.
 *
 * Phase 1 of the "Project as ecosystem of work" track (June 7, 2026).
 * Two coordinated layers, deliberately exposed side-by-side so the user
 * can tell which one drives what:
 *
 *   1. **Agent Mode** (``default_agent_mode_version_id``) — the
 *      reusable persona catalog row (``agent_modes`` table).
 *      Selecting one here pins the Mode that every NEW conversation
 *      inside this project will open with. Sourced from the existing
 *      ``useAgentModes`` hook — same dropdown semantics as the chat
 *      composer's Mode picker.
 *   2. **Istruzioni del progetto** (``instructions``) — free-form text
 *      APPENDED to the Mode's ``system_prompt_overlay`` for every turn
 *      of this project. Meant for the per-client / per-engagement
 *      context the Mode catalog can't carry (e.g. "Sul cliente Rossi
 *      SRL escludi sempre i documenti extra-UE").
 *
 * The BE composes them in this order — see
 * ``websocket_pydantic.py::project_persona.applied``:
 *
 *     final_overlay = mode_overlay  +  "\\n\\n"  +  project.instructions
 *
 * ``persona_role`` and ``persona_tone`` are UX hints stored alongside
 * (NOT injected into the prompt — avoids prompt-injection on the role
 * slot, see the BE pydantic model comment for the rationale).
 *
 * UX shape (single column, ~700px wide inside the workspace pane):
 *   ┌──────────────────────────────────────────────┐
 *   │ Istruzioni            Lingua libera         │
 *   │ ┌──────────────────────────────────────────┐ │
 *   │ │ <textarea autoresize, max 10k chars>     │ │
 *   │ └──────────────────────────────────────────┘ │
 *   │ X / 10.000 caratteri                        │
 *   │                                              │
 *   │ Ruolo                Tono                   │
 *   │ [Commercialista…]    [Formale ▾]            │
 *   │                                              │
 *   │ [Salva]  [Annulla]                          │
 *   └──────────────────────────────────────────────┘
 *
 * The form mounts in dirty-tracking mode: ``Salva`` is enabled only when
 * the values differ from the row currently in cache, and the cancel
 * button reverts to that snapshot. No optimistic update — the mutation
 * invalidates ``projectsQueryKey`` on success and the parent re-renders
 * from fresh server state.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import * as React from "react";

import type { Project } from "@ando/core/api";
import { Button, cn, toast } from "@ando/ui";

import { useAgentModes } from "@/features/agent-modes";

import { useUpdateProject } from "../../hooks/useProjects.js";

export interface PersonaSectionProps {
  readonly project: Project;
}

interface FormState {
  /** Empty string sentinel for "no Mode" — wire value is null. */
  agent_mode_version_id: string;
  instructions: string;
  persona_role: string;
  persona_tone: string;
}

const MAX_INSTRUCTIONS = 10_000;
const MAX_ROLE = 160;
const _MAX_TONE = 160;

const TONE_OPTIONS: ReadonlyArray<{
  readonly value: string;
  readonly labelKey: "formal" | "neutral" | "informal" | "expert";
}> = [
  { value: "formale", labelKey: "formal" },
  { value: "neutro", labelKey: "neutral" },
  { value: "colloquiale", labelKey: "informal" },
  { value: "tecnico", labelKey: "expert" },
];

function projectToFormState(project: Project): FormState {
  return {
    agent_mode_version_id: project.default_agent_mode_version_id ?? "",
    instructions: project.instructions ?? "",
    persona_role: project.persona_role ?? "",
    persona_tone: project.persona_tone ?? "",
  };
}

function isDirty(a: FormState, b: FormState): boolean {
  return (
    a.agent_mode_version_id !== b.agent_mode_version_id ||
    a.instructions !== b.instructions ||
    a.persona_role !== b.persona_role ||
    a.persona_tone !== b.persona_tone
  );
}

export const PersonaSection: React.FC<PersonaSectionProps> = ({ project }) => {
  const { i18n } = useLingui();
  const updateMutation = useUpdateProject();

  // Mode catalog — capability-gated. ``hasAccess=false`` (the tenant
  // never enabled ``modes``) hides the dropdown entirely so the form
  // collapses to the legacy textarea-only flow. We still keep the
  // ``agent_mode_version_id`` field in the form state so the row's
  // existing value round-trips untouched.
  const { modes, hasAccess: hasModeAccess } = useAgentModes();

  // Snapshot of the server-side state. The form draft is the local edit
  // buffer; ``Salva`` writes it back, ``Annulla`` reverts to this snapshot.
  const initial = React.useMemo(() => projectToFormState(project), [project]);
  const [draft, setDraft] = React.useState<FormState>(initial);

  // Re-seed the draft only when the underlying project ID swaps (navigated
  // to a different project). A row-level refetch of the SAME project does
  // NOT clobber the user's in-flight edits — much safer than gating on
  // "dirty" inside an effect (which would either need ``dirty`` in deps
  // and re-fire per keystroke, or fall back to ``eslint-disable`` which
  // is banned by the project's lint policy).
  const lastProjectIdRef = React.useRef(project.project_id);
  React.useEffect(() => {
    if (lastProjectIdRef.current !== project.project_id) {
      lastProjectIdRef.current = project.project_id;
      setDraft(initial);
    }
  }, [project.project_id, initial]);

  const dirty = isDirty(draft, initial);

  const handleSave = React.useCallback(() => {
    updateMutation.mutate(
      {
        projectId: project.project_id,
        patch: {
          default_agent_mode_version_id:
            draft.agent_mode_version_id.length > 0
              ? draft.agent_mode_version_id
              : null,
          instructions: draft.instructions.trim() || null,
          persona_role: draft.persona_role.trim() || null,
          persona_tone: draft.persona_tone.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success(i18n._(msg`Persona del progetto aggiornata`));
        },
        onError: (err) => {
          toast.error(
            err instanceof Error
              ? i18n._(msg`Impossibile salvare: ${err.message}`)
              : i18n._(msg`Impossibile salvare le modifiche`),
          );
        },
      },
    );
  }, [updateMutation, project.project_id, draft, i18n]);

  const handleCancel = React.useCallback(() => {
    setDraft(initial);
  }, [initial]);

  const charCount = draft.instructions.length;
  const overLimit = charCount > MAX_INSTRUCTIONS;

  // Resolve the currently-active mode label for the helper text below
  // the dropdown. Falls back to "nessuno" so the explainer copy always
  // forms a complete italian sentence.
  const activeModeName = React.useMemo(() => {
    if (!draft.agent_mode_version_id) return null;
    return (
      modes.find(
        (m) => m.current_version?.id === draft.agent_mode_version_id,
      )?.name ?? null
    );
  }, [draft.agent_mode_version_id, modes]);

  return (
    <div className="flex flex-col gap-5">
      {/* ── Mode picker (capability-gated) ──────────────────────────── */}
      {hasModeAccess ? (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="project-agent-mode"
            className="font-medium text-[12px] text-ink-2"
          >
            <Trans>Modo agente</Trans>
          </label>
          <StyledSelect
            id="project-agent-mode"
            value={draft.agent_mode_version_id}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                agent_mode_version_id: e.target.value,
              }))
            }
            aria-label={i18n._(msg`Modo agente per il progetto`)}
            className={cn(
              "h-9 rounded-md border border-hairline bg-white/85 px-3",
              "text-[13px] text-ink-1",
              "focus-visible:border-ando-orange/45 focus-visible:outline-none",
              "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
            )}
          >
            <option value="">{i18n._(msg`Nessun modo (predefinito)`)}</option>
            {modes
              .filter((m) => Boolean(m.current_version?.id))
              .map((m) => (
                <option
                  key={m.id}
                  // ``current_version.id`` is the wire field
                  // ``agent_mode_version_id`` the BE expects — same fix
                  // applied to the chat composer's mode picker (see
                  // ``route.tsx`` BUILTIN_STANDARD_MODE_DEF comment for
                  // the June 1, 2026 history).
                  value={m.current_version!.id}
                >
                  {m.name}
                </option>
              ))}
          </StyledSelect>
          <div className="text-[11px] text-ink-3 leading-relaxed">
            <Trans>
              Il Modo definisce chi è l'agente (riusabile su più progetti). Le
              istruzioni qui sotto si AGGIUNGONO al Modo solo per questo
              progetto.
            </Trans>
            {activeModeName ? (
              <>
                {" "}
                <span className="text-ink-2">
                  <Trans>Attivo: {activeModeName}.</Trans>
                </span>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="project-instructions"
          className="font-medium text-[12px] text-ink-2"
        >
          <Trans>Istruzioni per l'agente</Trans>
        </label>
        <textarea
          id="project-instructions"
          rows={6}
          value={draft.instructions}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, instructions: e.target.value }))
          }
          placeholder={i18n._(
            msg`Es. "Sei un commercialista esperto in IVA trimestrale. Quando produci un F24, escludi sempre i documenti extra-UE."`,
          )}
          aria-label={i18n._(msg`Istruzioni per l'agente nel progetto`)}
          className={cn(
            "rounded-md border border-hairline bg-white/85 px-3 py-2.5",
            "text-[13px] text-ink-1 leading-relaxed placeholder:text-ink-3",
            "focus-visible:border-ando-orange/45 focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
            "min-h-[140px] resize-y",
            overLimit && "border-error focus-visible:ring-error/30",
          )}
        />
        <div
          className={cn(
            "flex items-center justify-between gap-3 text-[11px] tabular-nums",
            overLimit ? "text-error" : "text-ink-3",
          )}
        >
          <span className="non-tabular-nums">
            {hasModeAccess ? (
              <Trans>
                Contesto specifico del progetto. Si aggiunge al Modo, non lo
                sostituisce.
              </Trans>
            ) : (
              <Trans>
                Vengono aggiunte al prompt di sistema solo per questo
                progetto.
              </Trans>
            )}
          </span>
          <span>
            {charCount.toLocaleString("it-IT")} /{" "}
            {MAX_INSTRUCTIONS.toLocaleString("it-IT")}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="project-persona-role"
            className="font-medium text-[12px] text-ink-2"
          >
            <Trans>Ruolo</Trans>
          </label>
          <input
            id="project-persona-role"
            type="text"
            value={draft.persona_role}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, persona_role: e.target.value }))
            }
            maxLength={MAX_ROLE}
            placeholder={i18n._(msg`Es. Commercialista, PM tecnico…`)}
            aria-label={i18n._(msg`Ruolo del progetto`)}
            className={cn(
              "h-9 rounded-md border border-hairline bg-white/85 px-3",
              "text-[13px] text-ink-1 placeholder:text-ink-3",
              "focus-visible:border-ando-orange/45 focus-visible:outline-none",
              "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
            )}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="project-persona-tone"
            className="font-medium text-[12px] text-ink-2"
          >
            <Trans>Tono</Trans>
          </label>
          <StyledSelect
            id="project-persona-tone"
            value={draft.persona_tone}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, persona_tone: e.target.value }))
            }
            aria-label={i18n._(msg`Tono del progetto`)}
            className={cn(
              "h-9 rounded-md border border-hairline bg-white/85 px-3",
              "text-[13px] text-ink-1",
              "focus-visible:border-ando-orange/45 focus-visible:outline-none",
              "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
            )}
          >
            <option value="">{i18n._(msg`Predefinito`)}</option>
            {TONE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.labelKey === "formal" && i18n._(msg`Formale`)}
                {opt.labelKey === "neutral" && i18n._(msg`Neutro`)}
                {opt.labelKey === "informal" && i18n._(msg`Colloquiale`)}
                {opt.labelKey === "expert" && i18n._(msg`Tecnico`)}
              </option>
            ))}
          </StyledSelect>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!dirty || overLimit || updateMutation.isPending}
        >
          {updateMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : null}
          <Trans>Salva</Trans>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={!dirty || updateMutation.isPending}
        >
          <Trans>Annulla</Trans>
        </Button>
      </div>
    </div>
  );
};

PersonaSection.displayName = "PersonaSection";
